import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, Copy, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import './PortalSignup.css';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [user, setUser] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      setError('No payment session found.');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;

    const verify = async () => {
      try {
        const { data } = await axios.get(`${API}/api/stripe/verify-session`, {
          params: { session_id: sessionId },
          withCredentials: true,
        });
        if (data.status === 'completed') {
          setUser(data.user);
          setTempPassword(data.user?.temp_password || '');
          setStatus('success');
        } else if (data.status === 'unpaid' && attempts < maxAttempts) {
          attempts++;
          setTimeout(verify, 2000);
        } else {
          setStatus('error');
          setError('Payment is still processing. Please check your email for portal access details.');
        }
      } catch (err) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(verify, 2000);
        } else {
          setStatus('error');
          setError(err.response?.data?.detail || 'Unable to verify payment. Please contact support.');
        }
      }
    };

    verify();
  }, [searchParams]);

  const handleCopy = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="signup-page" data-testid="payment-success-page">
      <Header />
      <div className="signup-container">
        <div className="signup-card">
          {status === 'verifying' && (
            <div className="signup-step-content success-center" data-testid="verifying">
              <Loader2 size={48} className="signup-spinner" />
              <h2>Confirming Your Payment</h2>
              <p className="signup-subtitle">Please wait while we set up your account...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="signup-step-content" data-testid="success-view">
              <div className="success-center">
                <CheckCircle size={56} className="success-check" />
                <h2>Welcome to R6 Advisory!</h2>
                <p className="signup-subtitle">Your payment is confirmed and your portal account is ready.</p>
              </div>
              <div className="success-credentials" data-testid="credentials-box">
                <h3>Your Portal Credentials</h3>
                <div className="success-cred-row">
                  <span className="success-cred-label">Email</span>
                  <span className="success-cred-value">{user?.email}</span>
                </div>
                <div className="success-cred-row">
                  <span className="success-cred-label">Temporary Password</span>
                  <div className="success-cred-pw">
                    <code data-testid="temp-password">{tempPassword}</code>
                    <button className="success-copy-btn" onClick={handleCopy} data-testid="copy-password">
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
                <p className="success-cred-note">Save these credentials. A copy has also been sent to your email.</p>
              </div>
              <div className="signup-actions success-actions">
                <button className="signup-btn primary" onClick={() => navigate('/portal/login')} data-testid="go-to-login">
                  Log In to Your Portal
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="signup-step-content success-center" data-testid="error-view">
              <AlertCircle size={48} className="error-icon" />
              <h2>Almost There</h2>
              <p className="signup-subtitle">{error}</p>
              <div className="signup-actions success-actions">
                <Link to="/portal/signup" className="signup-btn outline" data-testid="try-again">Try Again</Link>
                <a href="mailto:connect@r6collective.com" className="signup-btn primary" data-testid="contact-support">Contact Support</a>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
